import { useState, useEffect, useRef, useCallback, Suspense } from 'react';
import { Search, User, ShoppingBag, Phone, MapPin, Navigation, Menu, Heart } from 'lucide-react';
import { Link, useLocation } from 'wouter';
import { useCart } from '@/context/CartContext';
import { useClientReady } from '@/lib/initialRender';
import { useFavorites } from '@/context/FavoritesContext';
import { lazyWithReload } from '@/lib/lazyWithReload';

/* ── Lazy modals — loaded only on first open ── */
const SearchModal        = lazyWithReload(() => import('./SearchModal').then(m => ({ default: m.SearchModal })));
const CustomerModal      = lazyWithReload(() => import('./CustomerModal').then(m => ({ default: m.CustomerModal })));
const CartDrawerLazy     = lazyWithReload(() => import('./CartDrawer').then(m => ({ default: m.CartDrawer })));
const FavoritesDrawerLazy = lazyWithReload(() => import('./FavoritesDrawer').then(m => ({ default: m.FavoritesDrawer })));
const MobileMenuDrawerLazy = lazyWithReload(() => import('./MobileMenuDrawer').then(m => ({ default: m.MobileMenuDrawer })));
// The link stays mounted; only its dropdown loads after pointer or keyboard intent.
const MegaMenuPanel = lazyWithReload(() => import('./MegaMenu').then(m => ({ default: m.MegaMenuPanel })));
import type { MegaMenuContentProps } from './MegaMenu';
import {
  fetchMenu,
  type MenuItem,
} from '@/lib/shopifyApi.eager';
import { collectionUrl } from '@/lib/collectionUrl';
import { useCustomer } from '@/context/CustomerContext';
import { switchToPanel } from '@/lib/mobilePanels';
import { NAV_FALLBACK, createNavigationModel, normalizeCachedNavigation, normalizeShopifyNavigation, publishNavigation } from './navigationData';
import { BUKIETOWA_LOCATIONS } from '@/lib/globalFloristSchema';
import { scheduleNonCriticalTask } from '@/lib/nonCriticalTask';
import { isPrerenderMode } from '@/lib/isPrerenderMode';
import { BOUQUET_CREATOR_ENABLED, isUnavailableBouquetCreatorPath } from '@/lib/storeFeatures';


/* ─────────────────────────────────────────────
   DATA — edit names, phones, map links here
───────────────────────────────────────────── */
const SHOPS = BUKIETOWA_LOCATIONS.map(location => ({
  name: location.address.streetAddress,
  phone: location.telephone,
  phoneTel: `+${location.telephone.replace(/\D/g, '')}`,
  mapsUrl: location.pickupHref,
}));

/* ─────────────────────────────────────────────
   LOGO LOCKUP
───────────────────────────────────────────── */
function Logo({ onClick }: { onClick?: () => void }) {
  return (
    <Link
      href="/"
      className="flex items-center group flex-shrink-0"
      style={{ gap: '5px' }}
      data-testid="link-logo"
      onClick={onClick}
    >
      <picture>
        <source srcSet="/logo-80.webp 80w, /logo-128.webp 128w, /logo-256.webp 256w" sizes="(max-width: 767px) 40px, (max-width: 1023px) 96px, 128px" type="image/webp" />
        <img
          src="/logo.png"
          alt="Bukietowa"
          width={128}
          height={128}
          className="h-16 md:h-24 lg:h-32 w-16 md:w-24 lg:w-32 object-contain object-left flex-shrink-0 transition-transform duration-700 group-hover:scale-105"
        />
      </picture>
      <span
        className="font-serif text-[26px] md:text-[30px] lg:text-[32px] tracking-[0.04em] text-foreground transition-colors duration-300 group-hover:text-primary leading-none hidden sm:block"
        style={{ fontWeight: 400 }}
      >
        Bukietowa.pl
      </span>
    </Link>
  );
}

/* ─────────────────────────────────────────────
   NAV LINK
───────────────────────────────────────────── */
function NavLink({ label, href, onClick, disabled = false }: { label: string; href: string; onClick?: () => void; disabled?: boolean }) {
  if (disabled || isUnavailableBouquetCreatorPath(href)) {
    return (
      <button
        type="button"
        disabled
        aria-disabled="true"
        className="relative inline-flex items-center justify-center py-1 bg-transparent border-0 cursor-default"
        data-testid={`link-nav-${label.toLowerCase()}`}
      >
        <span
          className="text-[15px] tracking-[0.16em] uppercase text-[#1D1918]"
          style={{ fontFamily: 'Cormorant Garamond, Georgia, serif', fontWeight: 500 }}
        >
          {label}
        </span>
        <span
          className="absolute top-full -mt-0.5 left-1/2 -translate-x-1/2 whitespace-nowrap text-[11px] leading-3 tracking-[0.02em] text-[#9C3858]"
          style={{ fontFamily: 'DM Sans, sans-serif', fontWeight: 500 }}
        >
          Wkrótce
        </span>
      </button>
    );
  }

  return (
    <Link
      href={href}
      className="relative group/nav py-1"
      data-testid={`link-nav-${label.toLowerCase()}`}
      onClick={onClick}
    >
      <span
        className="text-[15px] tracking-[0.16em] uppercase text-black
                   group-hover/nav:text-[#D75B76] group-focus-visible/nav:text-[#D75B76] transition-colors duration-300"
        style={{ fontFamily: 'Cormorant Garamond, Georgia, serif', fontWeight: 500 }}
      >
        {label}
      </span>
    </Link>
  );
}


/** The eager trigger owns interaction state even while its panel downloads. */
function MegaNavTrigger({ label, href, ...content }: MegaMenuContentProps & { label: string; href: string }) {
  const [requested, setRequested] = useState(false);
  const [open, setOpen] = useState(false);
  const [compact, setCompact] = useState(false);
  const triggerRef = useRef<HTMLAnchorElement>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingFocus = useRef<'first' | 'last' | null>(null);

  const focusMenuLink = useCallback((panel: HTMLDivElement) => {
    const edge = pendingFocus.current;
    pendingFocus.current = null;
    // A delayed download must never take focus back after the visitor leaves.
    if (!edge || document.activeElement !== triggerRef.current) return;
    const links = panel.querySelectorAll<HTMLAnchorElement>('a[href]');
    const target = edge === 'first' ? links[0] : links[links.length - 1];
    target?.focus({ preventScroll: true });
  }, []);
  const setPanelRef = useCallback((panel: HTMLDivElement | null) => {
    panelRef.current = panel;
    if (panel) focusMenuLink(panel);
  }, [focusMenuLink]);

  const measure = useCallback(() => setCompact(window.innerWidth < 1240), []);
  const show = useCallback(() => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    measure();
    setRequested(true);
    setOpen(true);
  }, [measure]);
  const hideNow = useCallback(() => {
    pendingFocus.current = null;
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setOpen(false);
  }, []);
  const hideDelayed = useCallback(() => {
    pendingFocus.current = null;
    if (closeTimer.current) clearTimeout(closeTimer.current);
    // No panel exists while the chunk is pending: cancel that intent at once.
    // The grace period only bridges the pointer gap to an already visible panel.
    if (!panelRef.current) { setOpen(false); return; }
    closeTimer.current = setTimeout(() => setOpen(false), 250);
  }, []);

  useEffect(() => () => { if (closeTimer.current) clearTimeout(closeTimer.current); }, []);
  useEffect(() => {
    if (!requested) return;
    window.addEventListener('resize', measure, { passive: true });
    return () => window.removeEventListener('resize', measure);
  }, [requested, measure]);
  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') { triggerRef.current?.focus(); hideNow(); }
    };
    const onDown = (event: MouseEvent) => {
      if (!wrapRef.current?.contains(event.target as Node) && !panelRef.current?.contains(event.target as Node)) hideNow();
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDown);
    };
  }, [open, hideNow]);

  return (
    <div ref={wrapRef} onMouseEnter={show} onMouseLeave={hideDelayed}
      onBlur={event => {
        if (!wrapRef.current?.contains(event.relatedTarget as Node | null)
          && !panelRef.current?.contains(event.relatedTarget as Node | null)) hideDelayed();
      }}
      style={{ display: 'flex', alignItems: 'center' }}>
      <Link ref={triggerRef} href={href} aria-haspopup="true" aria-expanded={open}
        aria-controls={content.menuId} data-testid={`link-nav-${content.menuId.replace('mega-menu-', '')}`}
        onClick={hideNow} onFocus={show}
        onKeyDown={event => {
          if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
          event.preventDefault();
          pendingFocus.current = event.key === 'ArrowDown' ? 'first' : 'last';
          show();
          if (panelRef.current) focusMenuLink(panelRef.current);
        }}
        style={{ display: 'flex', alignItems: 'center', gap: 5, minHeight: 44, padding: '4px 0',
          background: 'none', border: 'none', cursor: 'pointer', position: 'relative', textDecoration: 'none' }}>
        <span style={{ fontFamily: 'Cormorant Garamond, Georgia, serif', fontWeight: 500, fontSize: 15,
          letterSpacing: '0.16em', textTransform: 'uppercase', color: open ? '#D75B76' : '#1D1918', transition: 'color 0.25s' }}>
          {label}
        </span>
      </Link>
      {requested && <Suspense fallback={null}>
        <MegaMenuPanel {...content} open={open} compact={compact} panelRef={setPanelRef}
          onShow={show} onHide={hideDelayed} onNavigate={hideNow} />
      </Suspense>}
    </div>
  );
}

/* ─────────────────────────────────────────────
   MAIN HEADER EXPORT
───────────────────────────────────────────── */
export function Header() {
  const clientReady = useClientReady();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [cartOpen, setCartOpen] = useState(false);
  const [cartEverOpened, setCartEverOpened] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchEverOpened, setSearchEverOpened] = useState(false);
  const [favoritesOpen, setFavoritesOpen] = useState(false);
  const [favoritesEverOpened, setFavoritesEverOpened] = useState(false);
  const [customerOpen, setCustomerOpen] = useState(false);
  const [customerEverOpened, setCustomerEverOpened] = useState(false);
  const [customerInitialTab, setCustomerInitialTab] = useState<'login' | 'register' | 'forgot'>('login');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileMenuEverOpened, setMobileMenuEverOpened] = useState(false);
  const [navItems, setNavItems] = useState<MenuItem[]>(() => {
    try {
      const c = localStorage.getItem('bk_nav_menu');
      if (c) {
        return normalizeCachedNavigation(JSON.parse(c) as MenuItem[]);
      }
    } catch {}
    return NAV_FALLBACK;
  });
  const headerRef = useRef<HTMLElement>(null);
  const [, setLocation] = useLocation();
  const { items, totalCount, removeItem, updateQuantity, proceedToCheckout } = useCart();
  const { customer, ensureSession } = useCustomer();
  const { favorites } = useFavorites();
  const navigation = createNavigationModel(clientReady ? navItems : NAV_FALLBACK);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 30);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 767px)');
    setIsMobile(mq.matches);
    const handleMq = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mq.addEventListener('change', handleMq);
    return () => mq.removeEventListener('change', handleMq);
  }, []);

  // Open customer modal programmatically from other pages (e.g. reset-hasla → forgot tab)
  // Also closes other mobile panels so account never overlaps with cart/menu/categories.
  useEffect(() => {
    const handle = (e: Event) => {
      const tab = (e as CustomEvent<{ tab?: 'login' | 'register' | 'forgot' }>).detail?.tab ?? 'login';
      // Close other mobile panels without triggering the account case again
      setCartOpen(false);
      setMobileMenuOpen(false);
      setSearchOpen(false);
      setFavoritesOpen(false);
      setCustomerInitialTab(tab);
      setCustomerEverOpened(true);
      setCustomerOpen(true);
    };
    window.addEventListener('open-customer-modal', handle);
    return () => window.removeEventListener('open-customer-modal', handle);
  }, []);

  // Translate legacy open-* events to the unified panel system
  useEffect(() => {
    const openCart = () => switchToPanel('cart');
    const openMenu = () => switchToPanel('menu');
    window.addEventListener('open-cart-drawer', openCart);
    window.addEventListener('open-mobile-menu', openMenu);
    return () => {
      window.removeEventListener('open-cart-drawer', openCart);
      window.removeEventListener('open-mobile-menu', openMenu);
    };
  }, []);

  // React to the unified mobile panel system
  useEffect(() => {
    let revision = 0;
    const handler = (e: Event) => {
      const currentRevision = ++revision;
      const panel = (e as CustomEvent<import('@/lib/mobilePanels').MobilePanel>).detail;
      if (panel === 'search') {
        setSearchEverOpened(true);
        setSearchOpen(true);
        setCartOpen(false);
        setMobileMenuOpen(false);
        setCustomerOpen(false);
        setFavoritesOpen(false);
      } else if (panel === 'favorites') {
        setFavoritesEverOpened(true);
        setFavoritesOpen(true);
        setSearchOpen(false);
        setCartOpen(false);
        setMobileMenuOpen(false);
        setCustomerOpen(false);
      } else if (panel === 'cart') {
        setCartEverOpened(true);
        setCartOpen(true);
        setMobileMenuOpen(false);
        setCustomerOpen(false);
        setSearchOpen(false);
        setFavoritesOpen(false);
      } else if (panel === 'menu') {
        setMobileMenuEverOpened(true);
        setMobileMenuOpen(true);
        setCartOpen(false);
        setCustomerOpen(false);
        setSearchOpen(false);
        setFavoritesOpen(false);
      } else if (panel === 'account') {
        void ensureSession().then(session => {
          if (revision !== currentRevision) return;
          if (session.customer) {
            setSearchOpen(false);
            setFavoritesOpen(false);
            setLocation('/konto');
          } else {
            setCustomerEverOpened(true);
            setCustomerInitialTab('login');
            setCustomerOpen(true);
            setCartOpen(false);
            setMobileMenuOpen(false);
            setSearchOpen(false);
            setFavoritesOpen(false);
          }
        });
      } else {
        // null or any other panel → close everything managed by Header
        setCartOpen(false);
        setMobileMenuOpen(false);
        setCustomerOpen(false);
        setSearchOpen(false);
        setFavoritesOpen(false);
      }
    };
    window.addEventListener('bk:mobile-panel', handler);
    return () => { revision += 1; window.removeEventListener('bk:mobile-panel', handler); };
  }, [ensureSession, setLocation]);

  // Track header height and expose as CSS custom property
  useEffect(() => {
    const el = headerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(entries => {
      const h = entries[0]?.contentRect.height;
      if (h) document.documentElement.style.setProperty('--header-height', `${h}px`);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    let cancelled = false;
    let started = false;
    const refreshNavigation = () => {
      if (started) return;
      started = true;
      void fetchMenu('menu-header').then(items => {
        if (!cancelled && items.length > 0) {
          const normalized = normalizeShopifyNavigation(items);
          setNavItems(normalized);
          publishNavigation(normalized);
        }
      }).catch(() => { /* Keep the complete cached/default navigation when offline. */ });
    };
    const cancel = isPrerenderMode ? (refreshNavigation(), () => {}) : scheduleNonCriticalTask(refreshNavigation);
    const el = headerRef.current;
    el?.addEventListener('pointerover', refreshNavigation, { once: true });
    el?.addEventListener('focusin', refreshNavigation, { once: true });
    return () => {
      cancelled = true;
      cancel();
      el?.removeEventListener('pointerover', refreshNavigation);
      el?.removeEventListener('focusin', refreshNavigation);
    };
  }, []);

  return (
    <>
      <header
        ref={headerRef}
        data-testid="header"
        className="fixed top-0 left-0 right-0 w-full z-50"
        style={{
          background: '#fff',
          boxShadow: isScrolled
            ? '0 4px 24px hsl(0 0% 0% / 0.06)'
            : 'none',
          paddingTop: 'env(safe-area-inset-top)',
          // Transition only the properties that actually change — NOT transition-all.
          // transition-all animates position/transform/will-change which can break
          // position:fixed on iOS Safari after back navigation.
          transition: 'background-color 500ms ease, backdrop-filter 500ms ease, box-shadow 500ms ease',
          // Force GPU compositing layer — keeps header fixed regardless of iOS
          // address-bar show/hide and back-navigation paint cycles.
          transform: 'translateZ(0)',
        }}
      >
        {/* Safari iOS top bleed mask — extends 100px above the fixed header */}
        <div
          className="md:hidden"
          style={{
            position: 'absolute',
            top: -100,
            left: 0,
            right: 0,
            bottom: 0,
            background: '#ffffff',
            pointerEvents: 'none',
            zIndex: 0,
          }}
        />
        {/* ── Top info bar — desktop/tablet only ── */}
        <div className="hidden md:block" style={{
          background: '#fff',
        }}>
          <div style={{ maxWidth: 1370, margin: '0 auto' }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              height: 36,
            }}>
              {/* ── Shops info ── */}
              <div style={{ display: 'flex', alignItems: 'center', overflow: 'hidden' }}>
                {SHOPS.map((shop, i) => (
                  <div key={i} style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    padding: '0 20px',
                    paddingLeft: i === 0 ? 0 : 20,
                    paddingRight: i === SHOPS.length - 1 ? 0 : 20,
                    borderRight: i < SHOPS.length - 1 ? '1px solid rgba(210,60,120,0.18)' : 'none',
                  }}>
                    <MapPin size={11} style={{ color: 'hsl(var(--primary))', flexShrink: 0 }} />
                    <span style={{
                      fontFamily: 'DM Sans, sans-serif',
                      fontSize: 11,
                      letterSpacing: '0.06em',
                      color: '#2a1f14',
                      whiteSpace: 'nowrap',
                    }}>
                      {shop.name}
                    </span>
                    <span style={{ color: '#555', fontSize: 10 }}>·</span>
                    <a href={`tel:${shop.phoneTel}`} style={{
                      display: 'flex', alignItems: 'center', gap: 5,
                      fontFamily: 'DM Sans, sans-serif',
                      fontSize: 11,
                      letterSpacing: '0.04em',
                      color: '#2a1f14',
                      textDecoration: 'none',
                      whiteSpace: 'nowrap',
                      transition: 'color 0.15s',
                    }}
                      onMouseEnter={e => (e.currentTarget.style.color = '#D75B76')}
                      onMouseLeave={e => (e.currentTarget.style.color = '#2a1f14')}
                    >
                      <Phone size={10} style={{ color: 'hsl(var(--primary))', flexShrink: 0 }} />
                      {shop.phone}
                    </a>
                    <span style={{ color: '#555', fontSize: 10 }}>·</span>
                    <a
                      href={shop.mapsUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        display: 'flex', alignItems: 'center', gap: 4,
                        fontFamily: 'DM Sans, sans-serif',
                        fontSize: 10,
                        letterSpacing: '0.1em',
                        textTransform: 'uppercase',
                        color: 'hsl(var(--primary))',
                        textDecoration: 'none',
                        whiteSpace: 'nowrap',
                        transition: 'color 0.15s',
                      }}
                      onMouseEnter={e => (e.currentTarget.style.color = '#2a1f14')}
                      onMouseLeave={e => (e.currentTarget.style.color = 'hsl(338, 75%, 62%)')}
                    >
                      <Navigation size={9} style={{ flexShrink: 0 }} />
                      Zobacz na mapie
                    </a>
                  </div>
                ))}
              </div>

            </div>
          </div>
        </div>

        {/* ── Mobile bar: hamburger | centered logo | search ── */}
        <div className="flex md:hidden items-center justify-between h-[56px] px-2" style={{ position: 'relative', zIndex: 1 }}>
          <button
            className="flex items-center justify-center min-w-[44px] min-h-[44px] text-foreground/70"
            aria-label="Otwórz menu"
            data-testid="button-mobile-menu-compact"
            onClick={() => switchToPanel('menu')}
          >
            <Menu strokeWidth={1.4} className="h-6 w-6" />
          </button>

          <Link
            href="/"
            aria-label="Bukietowa.pl - strona główna"
            data-testid="link-logo-mobile"
            style={{ position: 'absolute', left: '50%', transform: 'translateX(-50%)', display: 'flex', alignItems: 'center', gap: 6 }}
          >
            <picture>
              <source srcSet="/logo-80.webp 80w, /logo-128.webp 128w, /logo-256.webp 256w" sizes="(max-width: 767px) 40px, (max-width: 1023px) 96px, 128px" type="image/webp" />
              <img src="/logo.png" alt="Bukietowa" width={40} height={40} className="h-10 w-10 object-contain" />
            </picture>
            <span
              className="font-serif text-[26px] tracking-[0.04em] text-foreground leading-none"
              style={{ fontWeight: 400 }}
            >
              Bukietowa.pl
            </span>
          </Link>

          <button
            className="flex items-center justify-center min-w-[44px] min-h-[44px] text-foreground/70"
            aria-label="Szukaj"
            data-testid="button-mobile-search"
            onClick={() => switchToPanel('search')}
          >
            <Search strokeWidth={1.4} className="h-[22px] w-[22px]" />
          </button>
        </div>

        {/* ── Main navigation — tablet/desktop ── */}
        <div id="header-nav-inner" className="hidden md:block" style={{ maxWidth: 1370, margin: '0 auto' }}>
          <div className="flex items-center h-[72px] lg:h-[82px]">

            {/* Hamburger — mobile only */}
            <button
              className="lg:hidden flex items-center justify-center min-w-[44px] min-h-[44px] text-foreground/60 hover:text-foreground transition-colors duration-200 -ml-1"
              aria-label="Otwórz menu"
              data-testid="button-mobile-menu"
              onClick={() => switchToPanel('menu')}
            >
              <Menu strokeWidth={1.4} className="h-6 w-6" />
            </button>

            {/* Logo */}
            <Logo />

            {/* Nav links – desktop */}
            <nav
              className="hidden lg:flex items-center gap-9 ml-auto"
              data-testid="nav-main"
            >
              {navigation.topLevel.map((item) => {
                if (item.title === 'Kwiaty')
                  return (
                    <MegaNavTrigger key="kwiaty" label={item.title} href={collectionUrl('kwiaty')} sections={navigation.mega.kwiaty} menuId="mega-menu-kwiaty" featuredProduct={navigation.mega.kwiatyFeatured} featuredColumnTitle="Bestsellery" featuredLiveCollection="bestsellery" bukietyProducts={navigation.mega.bukietyProducts} />
                  );
                if (item.title === 'Okazje')
                  return (
                    <MegaNavTrigger key="okazje" label={item.title} href={collectionUrl('okazje')} sections={navigation.mega.okazje} menuId="mega-menu-okazje" featuredProduct={navigation.mega.okazjeFeatured} featuredColumnTitle="Polecane" featuredLiveCollection="polecane" />
                  );
                if (item.title === 'Kreator')
                  return <NavLink key="kreator" label={item.title} href="/kreatory-bukietow/" disabled={!BOUQUET_CREATOR_ENABLED} />;
                if (item.title === 'Pogrzeb')
                  return (
                    <MegaNavTrigger key="pogrzeb" label={item.title} href="/kompozycje-pogrzebowe/" pogrzebItems={navigation.mega.pogrzebItems} menuId="mega-menu-pogrzeb" />
                  );
                if (item.title === 'Ślub')
                  return <NavLink key="slub" label={item.title} href="/slub/" />;
                return <NavLink key={item.url} label={item.title} href={item.url} />;
              })}
            </nav>

            {/* ── Action icons — right side of nav bar ── */}
            <div className="hidden md:flex items-center ml-auto lg:ml-6" style={{ flexShrink: 0 }}>
              {/* Search */}
              <button
                onClick={() => switchToPanel('search')}
                aria-label="Szukaj"
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  width: 40, height: 40,
                  background: 'none', border: 'none', cursor: 'pointer',
                  color: '#000',
                  transition: 'color 0.18s, background 0.18s',
                  borderRadius: 8,
                  marginRight: 14,
                }}
                onMouseEnter={e => { e.currentTarget.style.color = 'hsl(338, 75%, 62%)'; }}
                onMouseLeave={e => { e.currentTarget.style.color = '#000'; }}
              >
                <Search size={24} strokeWidth={2} />
              </button>

              {/* Favourites */}
              <button
                type="button"
                onClick={() => switchToPanel('favorites')}
                aria-label="Ulubione"
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  width: 40, height: 40,
                  background: 'none', border: 'none', cursor: 'pointer',
                  color: '#000',
                  transition: 'color 0.18s',
                  borderRadius: 8,
                  position: 'relative',
                  marginRight: 4,
                  textDecoration: 'none',
                }}
                onMouseEnter={(e: React.MouseEvent<HTMLButtonElement>) => { e.currentTarget.style.color = 'hsl(338, 75%, 62%)'; }}
                onMouseLeave={(e: React.MouseEvent<HTMLButtonElement>) => { e.currentTarget.style.color = '#000'; }}
              >
                <Heart size={24} strokeWidth={2} />
                {favorites.length > 0 && (
                  <span style={{
                    position: 'absolute', top: 4, right: 4,
                    minWidth: 15, height: 15, borderRadius: 8,
                    background: 'hsl(var(--secondary))',
                    color: 'hsl(var(--secondary-foreground))',
                    fontSize: 9, fontWeight: 700,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    lineHeight: 1, padding: '0 2px',
                  }}>
                    {favorites.length}
                  </span>
                )}
              </button>

              {/* Account */}
              <button
                onClick={() => switchToPanel('account')}
                aria-label={customer ? customer.firstName || 'Konto' : 'Zaloguj się'}
                style={{
                  display: 'flex',
                  alignItems: 'center', justifyContent: 'center',
                  width: 40, height: 40,
                  background: 'none', border: 'none', cursor: 'pointer',
                  color: '#000',
                  transition: 'color 0.18s, background 0.18s',
                  borderRadius: 8,
                  position: 'relative',
                  marginRight: 4,
                }}
                onMouseEnter={e => { e.currentTarget.style.color = 'hsl(338, 75%, 62%)'; }}
                onMouseLeave={e => { e.currentTarget.style.color = '#000'; }}
              >
                <User size={24} strokeWidth={2} />
                {customer && (
                  <span style={{
                    position: 'absolute', top: 6, right: 6,
                    width: 5, height: 5, borderRadius: '50%',
                    background: 'hsl(var(--primary))',
                  }} />
                )}
              </button>

              {/* Cart */}
              <button
                onClick={() => switchToPanel('cart')}
                aria-label="Koszyk"
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'flex-end',
                  width: 40, height: 40,
                  background: 'none', border: 'none', cursor: 'pointer',
                  color: '#000',
                  transition: 'color 0.18s, background 0.18s',
                  borderRadius: 8,
                  position: 'relative',
                }}
                onMouseEnter={e => { e.currentTarget.style.color = 'hsl(338, 75%, 62%)'; }}
                onMouseLeave={e => { e.currentTarget.style.color = '#000'; }}
              >
                <ShoppingBag size={24} strokeWidth={2} />
                {totalCount > 0 && (
                  <span style={{
                    position: 'absolute', top: 4, right: 4,
                    minWidth: 15, height: 15, borderRadius: 8,
                    background: 'hsl(var(--secondary))',
                    color: 'hsl(var(--secondary-foreground))',
                    fontSize: 9, fontWeight: 700,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    lineHeight: 1, padding: '0 2px',
                  }}>
                    {totalCount}
                  </span>
                )}
              </button>
            </div>

          </div>
        </div>
      </header>

      {/* ── Mobile menu drawer — lazy: mounted only after first open, then kept in DOM ── */}
      {mobileMenuEverOpened && (
        <Suspense fallback={null}>
          <MobileMenuDrawerLazy
            open={mobileMenuOpen}
            onClose={() => { if (mobileMenuOpen) switchToPanel(null); }}
            navigation={navigation}
          />
        </Suspense>
      )}

      {/* ── Customer modal — lazy: mounted only after first open, then kept in DOM ── */}
      {customerEverOpened && (
        <Suspense fallback={null}>
          <CustomerModal
            open={customerOpen}
            onClose={() => { if (customerOpen) switchToPanel(null); }}
            initialTab={customerInitialTab}
          />
        </Suspense>
      )}

      {/* ── Search modal — lazy: mounted only after first open, then kept in DOM ── */}
      {searchEverOpened && (
        <Suspense fallback={null}>
          <SearchModal
            open={searchOpen}
            onClose={() => { if (searchOpen) switchToPanel(null); }}
          />
        </Suspense>
      )}

      {favoritesEverOpened && (
        <Suspense fallback={null}>
          <FavoritesDrawerLazy
            open={favoritesOpen}
            onClose={() => { if (favoritesOpen) switchToPanel(null); }}
          />
        </Suspense>
      )}

      {/* ── Cart drawer — lazy: mounted only after first open, then kept in DOM ── */}
      {cartEverOpened && (
        <Suspense fallback={null}>
          <CartDrawerLazy
            open={cartOpen}
            onClose={() => { if (cartOpen) switchToPanel(null); }}
            items={items}
            onRemove={removeItem}
            onUpdateQty={updateQuantity}
            onCheckout={() => {
              switchToPanel(null);
              proceedToCheckout();
            }}
          />
        </Suspense>
      )}
    </>
  );
}
